import { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
  TextInput,
  ActivityIndicator,
  ActionSheetIOS,
  Platform,
  Animated,
} from "react-native";
import { Image } from "expo-image";
import { platformAlert } from "../utils/platformAlert";
import { SafeAreaView } from "react-native-safe-area-context";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { Feather } from "@expo/vector-icons";
import { useNavigation, useIsFocused } from "@react-navigation/native";
import { spacing } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import * as ImagePicker from "expo-image-picker";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../types/navigation";
import * as SecureStore from "expo-secure-store";
import { useAuth } from "../hooks/useAuth";
import { uploadProfileImage } from "../utils/supabase/imageService";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { signOutRequest } from "../store/slices/authSlice";
import { useDispatch } from "react-redux";

type Picked = { uri: string; mimeType?: string };
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function ProfileScreen() {
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isFocused = useIsFocused();
  const colors = useColors();
  const styles = createStyles(colors);

  // Animations - start at final values to prevent dimming issues during hot reload
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  const [forceRefresh, setForceRefresh] = useState(0);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const { session } = useAuth();
  const [newUsername, setNewUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isVendorLoading, setIsVendorLoading] = useState(true);
  const [fullNameError, setFullNameError] = useState("");
  const [localVendor, setLocalVendor] = useState<any>(null); // Local state for vendor

  const { data: profile, refetch: refetchProfile } = useQuery({
    queryKey: ["profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      return data;
    },
    enabled: !!session?.user?.id,
  });

  // Fetch default delivery address for phone number display
  const { data: defaultDeliveryAddress } = useQuery({
    queryKey: ["default-delivery-address", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data } = await supabase
        .from("delivery_addresses")
        .select("phone_number")
        .eq("user_id", session.user.id)
        .eq("is_default", true)
        .single();
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const { data: vendor, isLoading: vendorIsLoading } = useQuery({
    queryKey: ["vendor", session?.user?.id, forceRefresh],
    queryFn: async () => {
      if (!session?.user?.id) return null;

      setIsVendorLoading(true);
      console.log(
        "[ProfileScreen] Checking for vendor with user ID:",
        session.user.id
      );

      // First check if there are ANY active (non-deleted) vendors
      const { data: allVendors, error: checkError } = await supabase
        .from("vendors")
        .select("id, store_name, status, created_at, is_deleted")
        .eq("user_id", session.user.id)
        .neq("status", "deleted")
        .not("is_deleted", "eq", true);

      if (allVendors && allVendors.length > 0) {
        console.log("[ProfileScreen] All vendors found:", allVendors);
      } else {
        console.log("[ProfileScreen] No vendors found for user");
      }

      // Get the most recent vendor for this user (excluding deleted vendors)
      const { data, error } = await supabase
        .from("vendors")
        .select("*")
        .eq("user_id", session.user.id)
        .neq("status", "deleted")
        .not("is_deleted", "eq", true)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) {
        console.error("[ProfileScreen] Error fetching vendor:", error);
        setIsVendorLoading(false);
        return null;
      }

      if (!data || data.length === 0) {
        console.log("[ProfileScreen] No vendor found after query");
        setIsVendorLoading(false);
        return null;
      }

      console.log(
        "[ProfileScreen] Using vendor:",
        data[0].id,
        "Status:",
        data[0].status
      );
      setLocalVendor(data[0]);
      setIsVendorLoading(false);
      return data[0];
    },
    enabled: !!session?.user?.id,
  });

  // Update isVendorLoading when the vendor query is loading
  useEffect(() => {
    setIsVendorLoading(vendorIsLoading);
  }, [vendorIsLoading]);

  // Force refetch when screen comes into focus
  useEffect(() => {
    if (isFocused) {
      console.log("[ProfileScreen] is focused - refetching vendor data");
      setForceRefresh((prev) => prev + 1); // This will force the query to re-run

      // Invalidate the vendor and delivery address cache
      if (session?.user?.id) {
        queryClient.invalidateQueries({
          queryKey: ["vendor", session.user.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["default-delivery-address", session.user.id],
        });
      }
    }
  }, [isFocused, queryClient, session?.user?.id]);

  useEffect(() => {
    if (profile) {
      setNewUsername(profile.username || "");
      setFullName(profile.full_name || "");
    }
  }, [profile]);

  const handleTermsOfService = () => {
    navigation.navigate("TermsAndConditions" as never);
  };

  const handlePrivacyPolicy = () => {
    navigation.navigate("PrivacyPolicy" as never);
  };

  const handleSignOut = async () => {
    platformAlert("Sign Out", "Are you sure you want to sign out?", [
      {
        text: "Cancel",
        style: "cancel",
      },
      {
        text: "Sign Out",
        style: "destructive",
        onPress: async () => {
          try {
            // First sign out from Supabase
            await supabase.auth.signOut();

            // Clear all storage keys related to Supabase Auth
            const authKeys = [
              "supabase.auth.token",
              "supabase.auth.refreshToken",
              "supabase.auth.user",
              "supabase.auth.expires_at",
              "supabase.auth.provider_token",
              "supabase.auth.provider_refresh_token",
            ];

            for (const key of authKeys) {
              await SecureStore.deleteItemAsync(key).catch(() => {});
            }
            dispatch(signOutRequest());
            console.log(
              "Successfully signed out from Supabase and cleared storage"
            );
          } catch (error) {
            console.error("Error during sign out:", error);
            // Still try to navigate even if there was an error
            navigation.reset({
              index: 0,
              routes: [{ name: "Auth" }],
            });
          }
        },
      },
    ]);
  };

  const handleProfileHeaderPress = () => {
    // Only set the values from the current user's profile
    if (profile) {
      setNewUsername(profile.username || "");
      setFullName(profile.full_name || "");

      console.log("[ProfileScreen] Loading profile data:", {
        username: profile.username || "",
        fullName: profile.full_name || "",
        userId: session?.user?.id,
      });
    } else {
      // If no profile exists, reset all fields
      setNewUsername("");
      setFullName("");
    }

    // Reset any error states
    setUsernameError("");
    setFullNameError("");
    setProfileModalVisible(true);
  };

  const checkUsernameAvailability = async (username: string) => {
    if (!username.trim()) {
      setUsernameError("Username cannot be empty");
      return false;
    }

    // Username format validation (letters, numbers, underscores)
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (!usernameRegex.test(username)) {
      setUsernameError(
        "Username can only contain letters, numbers, and underscores"
      );
      return false;
    }

    setIsCheckingUsername(true);

    try {
      // Check if username already exists in the database
      const { data, error } = await supabase
        .from("profiles")
        .select("username")
        .eq("username", username.toLowerCase())
        .neq("id", session?.user?.id) // Exclude current user
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 means no rows returned
        throw error;
      }

      if (data) {
        setUsernameError("This username is already taken");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking username:", error);
      setUsernameError("Error checking username availability");
      return false;
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const pickFromLibrary = async (
    allowsEditing: boolean
  ): Promise<Picked | null> => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      platformAlert(
        "Permission Denied",
        "Permission to access media library is required"
      );
      return null;
    }

    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing,
      aspect: allowsEditing ? [1, 1] : undefined, // native cropper when true
      quality: 0.9,
    });
    if (res.canceled || !res.assets?.length) return null;

    const a = res.assets[0];
    return { uri: a.uri, mimeType: a.mimeType };
  };

  const handleSelectImage = async () => {
    try {
      const choose = async (crop: boolean) => {
        const picked = await pickFromLibrary(crop);
        if (picked) {
          setSelectedImage(picked.uri);
        }
      };

      platformAlert("Change Profile Image", "Would you like to change the image?", [
        { text: "Cancel", style: "cancel" },
        { text: "OK", onPress: () => choose(false) },
      ]);
    } catch (e) {
      console.error("Error selecting image:", e);
      platformAlert("Error", "Failed to select image");
    }
  };

  const validateFullName = (name: string): boolean => {
    // Split the name into words and remove any extra whitespace
    const words = name.trim().split(/\s+/);

    // Check if we have at least 2 words (first and last name)
    if (words.length < 2) {
      setFullNameError("Please enter both your first and last name");
      return false;
    }

    // Take only the first and last words if there are more
    const [firstName, lastName] = [words[0], words[words.length - 1]];

    // Function to properly capitalize names with special patterns
    const formatName = (name: string): string => {
      // First, lowercase everything
      let formatted = name.toLowerCase();

      // Capitalize first letter
      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);

      // Handle special prefix cases (Mc, Mac, O', De, etc.)
      formatted = formatted.replace(
        /\b(mc|mac|o'|de|van|von|la|le|di)\w/gi,
        (match) => {
          const prefix = match.slice(0, -1);
          const letter = match.slice(-1);
          return (
            prefix.charAt(0).toUpperCase() +
            prefix.slice(1) +
            letter.toUpperCase()
          );
        }
      );

      return formatted;
    };

    // Format both names
    const formattedFirstName = formatName(firstName);
    const formattedLastName = formatName(lastName);

    // Update the full name with proper capitalization
    setFullName(`${formattedFirstName} ${formattedLastName}`);
    setFullNameError("");
    return true;
  };

  const saveProfile = async () => {
    try {
      let updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Validate full name if provided
      if (fullName && fullName.trim()) {
        if (!validateFullName(fullName)) {
          return;
        }
      }

      console.log("[ProfileScreen] Starting profile update with data:", {
        fullName: fullName || "",
        username: newUsername,
        hasNewImage: !!selectedImage,
        userId: session?.user?.id,
      });

      // Only run username validation if username has changed AND user entered a new username
      const currentUsername = profile?.username || "";
      const trimmedNewUsername = newUsername.trim();
      
      if (trimmedNewUsername !== currentUsername) {
        // Only validate if user is setting a new username (not clearing it)
        if (trimmedNewUsername) {
          if (!(await checkUsernameAvailability(trimmedNewUsername))) {
            return;
          }
          updateData.username = trimmedNewUsername.toLowerCase();
        } else {
          // User is clearing their username - allow it
          updateData.username = null;
        }
      }

      // Update full name - if empty or only whitespace, set to null
      updateData.full_name = fullName && fullName.trim() ? fullName : null;
      console.log("[ProfileScreen] Full name being saved:", {
        value: fullName || "",
        forUber: true,
        forDatabase: true,
        userId: session?.user?.id,
      });

      // If we have a new profile image, upload it
      if (selectedImage && session?.user?.id) {
        try {
          setIsUploadingImage(true);
          const publicUrl = await uploadProfileImage(selectedImage, session.user.id);
          updateData.avatar_url = publicUrl;
        } catch (error) {
          console.error("Error uploading profile image:", error);
          platformAlert(
            "Error",
            "Failed to upload profile image. Other fields will still be updated."
          );
        } finally {
          setIsUploadingImage(false);
        }
      }

      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", session?.user?.id);

      if (error) throw error;

      await queryClient.invalidateQueries({
        queryKey: ["profile", session?.user?.id],
      });

      setProfileModalVisible(false);
      setSelectedImage(null);
      platformAlert("Success", "Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      platformAlert("Error", "Failed to update profile");
    }
  };

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.container,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
          {/* Profile Header Section */}
          <View style={styles.welcomeSection}>
            <Text style={styles.welcomeGreeting}>Account</Text>
            <Text style={styles.welcomeSubtext}>Manage your profile and settings</Text>
          </View>

          {/* Profile Header Card */}
          <TouchableOpacity
            style={styles.profileCard}
            onPress={handleProfileHeaderPress}
            activeOpacity={0.7}
          >
            <View style={styles.profileInfo}>
              <View style={styles.avatarWrapper}>
                {profile?.avatar_url ? (
                  <Image
                    source={profile.avatar_url}
                    style={styles.avatar}
                    contentFit="cover"
                    cachePolicy="memory-disk"
                  />
                ) : (
                  <View style={styles.avatarPlaceholder}>
                    <Feather name="user" size={28} color="#FFFFFF" />
                  </View>
                )}
                <View style={styles.editBadge}>
                  <Feather name="edit-2" size={10} color="black" />
                </View>
              </View>
              <View style={styles.userTextContainer}>
                <Text style={styles.usernameText} numberOfLines={1}>
                  {profile?.username
                    ? `@${profile.username}`
                    : session?.user?.email}
                </Text>
                {profile?.username && (
                  <Text style={styles.emailText} numberOfLines={1}>{session?.user?.email}</Text>
                )}
              </View>
              <Feather name="chevron-right" size={20} color={colors.textLight} />
            </View>
          </TouchableOpacity>

          {/* Profile Edit Modal - Use View overlay on web, Modal on native */}
          {Platform.OS === 'web' ? (
            profileModalVisible && (
              <View style={styles.webModalOverlay}>
                <KeyboardAwareScrollView style={styles.modalScroll}>
                  <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                      <Text style={styles.modalTitle}>Edit Profile</Text>
                      <TouchableOpacity
                        onPress={() => setProfileModalVisible(false)}
                        style={styles.closeButton}
                      >
                        <Feather name="x" size={24} color={colors.textLight} />
                      </TouchableOpacity>
                    </View>

                    {/* Profile Image Section */}
                    <View style={styles.profileImageSectionCompact}>
                      <TouchableOpacity
                        onPress={handleSelectImage}
                        style={styles.imageSelector}
                        disabled={isUploadingImage}
                      >
                        <View style={styles.modalAvatarContainer}>
                          {isUploadingImage ? (
                            <View style={styles.loadingOverlay}>
                              <ActivityIndicator
                                size="large"
                                color={colors.primary}
                              />
                            </View>
                          ) : selectedImage ? (
                            <Image
                              source={selectedImage}
                              style={styles.modalAvatar}
                              contentFit="cover"
                            />
                          ) : profile?.avatar_url ? (
                            <Image
                              source={profile.avatar_url}
                              style={styles.modalAvatar}
                              contentFit="cover"
                              cachePolicy="memory-disk"
                            />
                          ) : (
                        <View style={styles.modalAvatarPlaceholder}>
                          <Feather
                            name="user"
                            size={40}
                            color="#FFFFFF"
                          />
                        </View>
                          )}
                        </View>
                        {!isUploadingImage && (
                          <View style={styles.imageHintRow}>
                            <Text style={styles.imageHint}>
                              Tap to change profile image
                            </Text>
                            <View style={styles.editIconButton}>
                              <Feather name="edit-2" size={12} color="#000000" />
                            </View>
                          </View>
                        )}
                      </TouchableOpacity>
                    </View>

                    {/* Account Information Section */}
                    <View style={styles.accountInfoSection}>
                      <Text style={styles.sectionLabel}>ACCOUNT INFORMATION</Text>

                      <Text style={styles.accountInfoLabel}>Email</Text>
                      <Text style={styles.accountInfoValue}>
                        {session?.user?.email || "No email provided"}
                      </Text>

                      <Text style={styles.accountInfoLabel}>Phone Number</Text>
                      <Text style={styles.accountInfoValue}>
                        {defaultDeliveryAddress?.phone_number ||
                          "No phone number provided"}
                      </Text>

                      <Text style={styles.accountInfoLabel}>Member Since</Text>
                      <Text style={styles.accountInfoValue}>
                        {session?.user?.created_at
                          ? new Date(session.user.created_at).toLocaleDateString()
                          : "Unknown"}
                      </Text>
                    </View>

                    {/* Username Section */}
                    <View style={styles.usernameSection}>
                      <Text style={styles.sectionLabel}>USERNAME</Text>
                      <TextInput
                        style={[
                          styles.input,
                          usernameError ? styles.inputError : null,
                        ]}
                        value={newUsername}
                        onChangeText={setNewUsername}
                        placeholder="Enter username"
                        autoCapitalize="none"
                        autoCorrect={false}
                      />

                      {usernameError ? (
                        <Text style={styles.errorText}>{usernameError}</Text>
                      ) : (
                        <Text style={styles.usernameHint}>
                          Choose a unique username (letters, numbers, underscores
                          only)
                        </Text>
                      )}
                    </View>

                    {/* Full Name Section */}
                    <View style={styles.sectionNoMargin}>
                      <Text style={styles.sectionLabel}>FULL NAME</Text>
                      <TextInput
                        style={[
                          styles.input,
                          fullNameError ? styles.inputError : null,
                        ]}
                        value={fullName}
                        onChangeText={(text) => {
                          setFullName(text);
                          if (fullNameError) setFullNameError("");
                        }}
                        placeholder="Enter your full name"
                        autoCapitalize="words"
                      />
                      {fullNameError ? (
                        <Text style={styles.errorText}>{fullNameError}</Text>
                      ) : (
                        <Text style={styles.hint}>
                          Enter your full name (e.g., John Smith)
                        </Text>
                      )}

                      <View style={styles.modalButtonsCompact}>
                        <TouchableOpacity
                          style={[styles.modalButton, styles.cancelButton]}
                          onPress={() => {
                            setProfileModalVisible(false);
                            setSelectedImage(null);
                          }}
                          disabled={isUploadingImage || isCheckingUsername}
                        >
                          <Text style={styles.cancelButtonText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                          style={[
                            styles.modalButton,
                            styles.saveButton,
                            (isCheckingUsername || isUploadingImage) &&
                              styles.disabledButton,
                          ]}
                          onPress={saveProfile}
                          disabled={isCheckingUsername || isUploadingImage}
                        >
                          {isCheckingUsername || isUploadingImage ? (
                            <ActivityIndicator size="small" color={colors.surface} />
                          ) : (
                            <Text style={styles.buttonText}>Save Changes</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                </KeyboardAwareScrollView>
              </View>
            )
          ) : (
            <Modal
              visible={profileModalVisible}
              transparent={true}
              animationType="slide"
              onRequestClose={() => setProfileModalVisible(false)}
            >
              <View style={styles.modalOverlay}>
                <KeyboardAwareScrollView style={styles.modalScroll}>
                  <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                      <Text style={styles.modalTitle}>Edit Profile</Text>
                      <TouchableOpacity
                        onPress={() => setProfileModalVisible(false)}
                        style={styles.closeButton}
                      >
                        <Feather name="x" size={24} color={colors.textLight} />
                      </TouchableOpacity>
                    </View>

                    {/* Profile Image Section */}
                    <View style={styles.profileImageSectionCompact}>
                      <TouchableOpacity
                        onPress={handleSelectImage}
                        style={styles.imageSelector}
                        disabled={isUploadingImage}
                      >
                        <View style={styles.modalAvatarContainer}>
                          {isUploadingImage ? (
                            <View style={styles.loadingOverlay}>
                              <ActivityIndicator
                                size="large"
                                color={colors.primary}
                              />
                            </View>
                          ) : selectedImage ? (
                            <Image
                              source={selectedImage}
                              style={styles.modalAvatar}
                              contentFit="cover"
                            />
                          ) : profile?.avatar_url ? (
                            <Image
                              source={profile.avatar_url}
                              style={styles.modalAvatar}
                              contentFit="cover"
                              cachePolicy="memory-disk"
                            />
                          ) : (
                        <View style={styles.modalAvatarPlaceholder}>
                          <Feather
                            name="user"
                            size={40}
                            color="#FFFFFF"
                          />
                        </View>
                          )}
                        </View>
                        {!isUploadingImage && (
                          <View style={styles.imageHintRow}>
                            <Text style={styles.imageHint}>
                              Tap to change profile image
                            </Text>
                            <View style={styles.editIconButton}>
                              <Feather name="edit-2" size={12} color="#000000" />
                            </View>
                          </View>
                        )}
                      </TouchableOpacity>
                    </View>

                    {/* Account Information Section */}
                    <View style={styles.accountInfoSection}>
                      <Text style={styles.sectionLabel}>ACCOUNT INFORMATION</Text>

                      <Text style={styles.accountInfoLabel}>Email</Text>
                      <Text style={styles.accountInfoValue}>
                        {session?.user?.email || "No email provided"}
                      </Text>

                      <Text style={styles.accountInfoLabel}>Phone Number</Text>
                      <Text style={styles.accountInfoValue}>
                        {defaultDeliveryAddress?.phone_number ||
                          "No phone number provided"}
                      </Text>

                      <Text style={styles.accountInfoLabel}>Member Since</Text>
                      <Text style={styles.accountInfoValue}>
                        {session?.user?.created_at
                          ? new Date(session.user.created_at).toLocaleDateString()
                          : "Unknown"}
                      </Text>
                    </View>

                    {/* Username Section */}
                    <View style={styles.usernameSection}>
                      <Text style={styles.sectionLabel}>USERNAME</Text>
                      <TextInput
                        style={[
                          styles.input,
                          usernameError ? styles.inputError : null,
                        ]}
                        value={newUsername}
                        onChangeText={setNewUsername}
                        placeholder="Enter username"
                        autoCapitalize="none"
                        autoCorrect={false}
                      />

                      {usernameError ? (
                        <Text style={styles.errorText}>{usernameError}</Text>
                      ) : (
                        <Text style={styles.usernameHint}>
                          Choose a unique username (letters, numbers, underscores
                          only)
                        </Text>
                      )}
                    </View>

                    {/* Full Name Section */}
                    <View style={styles.sectionNoMargin}>
                      <Text style={styles.sectionLabel}>FULL NAME</Text>
                      <TextInput
                        style={[
                          styles.input,
                          fullNameError ? styles.inputError : null,
                        ]}
                        value={fullName}
                        onChangeText={(text) => {
                          setFullName(text);
                          if (fullNameError) setFullNameError("");
                        }}
                        placeholder="Enter your full name"
                        autoCapitalize="words"
                      />
                      {fullNameError ? (
                        <Text style={styles.errorText}>{fullNameError}</Text>
                      ) : (
                        <Text style={styles.hint}>
                          Enter your full name (e.g., John Smith)
                        </Text>
                      )}

                      <View style={styles.modalButtonsCompact}>
                        <TouchableOpacity
                          style={[styles.modalButton, styles.cancelButton]}
                          onPress={() => {
                            setProfileModalVisible(false);
                            setSelectedImage(null);
                          }}
                          disabled={isUploadingImage || isCheckingUsername}
                        >
                          <Text style={styles.cancelButtonText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                          style={[
                            styles.modalButton,
                            styles.saveButton,
                            (isCheckingUsername || isUploadingImage) &&
                              styles.disabledButton,
                          ]}
                          onPress={saveProfile}
                          disabled={isCheckingUsername || isUploadingImage}
                        >
                          {isCheckingUsername || isUploadingImage ? (
                            <ActivityIndicator size="small" color={colors.surface} />
                          ) : (
                            <Text style={styles.buttonText}>Save Changes</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                </KeyboardAwareScrollView>
              </View>
            </Modal>
          )}

          {/* Menu Sections */}
          <View style={styles.menuContainer}>
            <Text style={styles.menuSectionTitle}>General</Text>
            <View style={styles.menuCard}>
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => navigation.navigate("Likes")}
              >
                <View style={styles.iconContainer}>
                  <Feather name="heart" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Liked Products</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => navigation.navigate("Orders")}
              >
                <View style={styles.iconContainer}>
                  <Feather name="package" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Orders</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  if (localVendor) {
                    navigation.navigate("VendorSettings");
                  } else {
                    navigation.navigate("VendorRegistration");
                  }
                }}
              >
                <View style={styles.iconContainer}>
                  <Feather name="settings" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>
                  {localVendor ? "Store Settings" : "Become a Vendor"}
                </Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.menuItem, { borderBottomWidth: 0 }]}
                onPress={() => navigation.navigate("PaymentMethods")}
              >
                <View style={styles.iconContainer}>
                  <Feather name="credit-card" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Payment Methods</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>
            </View>

            <Text style={styles.menuSectionTitle}>App Settings</Text>
            <View style={styles.menuCard}>
              {Platform.OS !== 'web' && (
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => navigation.navigate("NotificationSettings" as never)}
                >
                  <View style={styles.iconContainer}>
                    <Feather name="bell" size={20} color={colors.text} />
                  </View>
                  <Text style={styles.menuText}>Notifications</Text>
                  <Feather name="chevron-right" size={18} color={colors.textLight} />
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => navigation.navigate("Support" as never)}
              >
                <View style={styles.iconContainer}>
                  <Feather name="help-circle" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Support</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => navigation.navigate("PasswordReset" as never)}
              >
                <View style={styles.iconContainer}>
                  <Feather name="key" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Reset Password</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.menuItem, { borderBottomWidth: 0 }]}
                onPress={() => navigation.navigate("Tutorial" as never)}
              >
                <View style={styles.iconContainer}>
                  <Feather name="book-open" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Tutorial</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>
            </View>

            <Text style={styles.menuSectionTitle}>Info</Text>
            <View style={styles.menuCard}>
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => navigation.navigate("About")}
              >
                <View style={styles.iconContainer}>
                  <Feather name="info" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>About JadeTrade</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => navigation.navigate("PaymentProcessingInfo" as never)}
              >
                <View style={styles.iconContainer}>
                  <Feather name="dollar-sign" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Payment Processing</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.menuItem}
                onPress={handleTermsOfService}
              >
                <View style={styles.iconContainer}>
                  <Feather name="file-text" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Terms and Conditions</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.menuItem, { borderBottomWidth: 0 }]} 
                onPress={handlePrivacyPolicy}
              >
                <View style={styles.iconContainer}>
                  <Feather name="shield" size={20} color={colors.text} />
                </View>
                <Text style={styles.menuText}>Privacy Policy</Text>
                <Feather name="chevron-right" size={18} color={colors.textLight} />
              </TouchableOpacity>
            </View>

            <View style={[styles.menuCard, { marginTop: spacing.lg, marginBottom: spacing.xl }]}>
              <TouchableOpacity
                style={styles.menuItem}
                onPress={handleSignOut}
              >
                <View style={styles.iconContainer}>
                  <Feather name="log-out" size={20} color={colors.error} />
                </View>
                <Text style={[styles.menuText, { color: colors.error }]}>Sign Out</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.menuItem, { borderBottomWidth: 0 }]}
                onPress={() => navigation.navigate("DeleteAccount" as never)}
              >
                <View style={styles.iconContainer}>
                  <Feather name="trash-2" size={20} color={colors.error} />
                </View>
                <Text style={[styles.menuText, { color: colors.error }]}>Delete Account</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </Animated.View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
    },
    welcomeGreeting: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    welcomeSubtext: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 2,
      fontWeight: "500",
    },
    profileCard: {
      marginHorizontal: spacing.lg,
      marginTop: spacing.md,
      marginBottom: spacing.lg,
      backgroundColor: colors.surface,
      borderRadius: 24,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.03,
      shadowRadius: 8,
      elevation: 2,
    },
    profileInfo: {
      flexDirection: "row",
      alignItems: "center",
    },
    avatarWrapper: {
      position: 'relative',
    },
    avatar: {
      width: 64,
      height: 64,
      borderRadius: 22,
      backgroundColor: colors.background,
    },
    avatarPlaceholder: {
      width: 64,
      height: 64,
      borderRadius: 22,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    editBadge: {
      position: 'absolute',
      bottom: -2,
      right: -2,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: colors.primary,
      borderWidth: 2,
      borderColor: colors.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    userTextContainer: {
      flex: 1,
      marginLeft: spacing.md,
    },
    usernameText: {
      fontSize: 15,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: -0.3,
    },
    emailText: {
      fontSize: 13,
      color: colors.textLight,
      marginTop: 2,
    },
    menuContainer: {
      paddingHorizontal: spacing.lg,
    },
    menuSectionTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.textLight,
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginLeft: 4,
      marginBottom: spacing.sm,
      marginTop: spacing.md,
    },
    menuCard: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
      marginBottom: spacing.md,
    },
    menuItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    iconContainer: {
      width: 38,
      height: 38,
      borderRadius: 12,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      marginRight: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    menuText: {
      flex: 1,
      fontSize: 16,
      fontWeight: "500",
      color: colors.text,
    },
    signOutButton: {
      marginTop: 0,
    },
    signOutText: {
      color: colors.error,
      fontWeight: "600",
    },
    deleteAccountButton: {
      borderBottomWidth: 0,
    },
    deleteAccountText: {
      color: colors.error,
      fontWeight: "600",
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: colors.background,
    },
    webModalOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.background,
      zIndex: 1000,
      flex: 1,
      overflow: 'hidden',
    },
    modalScroll: {
      flex: 1,
      paddingTop: spacing.xl,
      ...(Platform.OS === 'web' ? { height: '100%' } : {}),
    },
    modalContent: {
      paddingHorizontal: spacing.lg,
      ...(Platform.OS === 'web' ? { paddingBottom: 100 } : {}),
    },
    modalHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.lg,
      paddingTop: spacing.xl,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      position: "relative",
    },
    closeButton: {
      position: "absolute",
      right: spacing.sm,
      top: spacing.lg,
      padding: spacing.sm,
      zIndex: 99,
    },
    modalTitle: {
      fontSize: 22,
      fontWeight: "800",
      color: colors.text,
      textAlign: "center",
      letterSpacing: -0.5,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 12,
      padding: spacing.md,
      fontSize: 16,
      backgroundColor: colors.surface,
      color: colors.text,
      marginTop: spacing.xs,
    },
    inputError: {
      borderColor: colors.error,
    },
    errorText: {
      color: colors.error,
      fontSize: 14,
      marginBottom: spacing.md,
    },
    modalButton: {
      flex: 1,
      padding: spacing.md,
      borderRadius: 16,
      alignItems: "center",
      marginHorizontal: spacing.xs,
    },
    cancelButton: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    saveButton: {
      backgroundColor: colors.primary,
    },
    disabledButton: {
      opacity: 0.7,
    },
    buttonText: {
      fontSize: 16,
      fontWeight: "700",
      color: "#000",
    },
    profileImageSectionCompact: {
      alignItems: "center",
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    imageSelector: {
      alignItems: "center",
    },
    modalAvatarContainer: {
      width: 120,
      height: 120,
      borderRadius: 40,
      overflow: "hidden",
      position: "relative",
      borderWidth: 2,
      borderColor: colors.border,
      marginBottom: spacing.sm,
    },
    modalAvatar: {
      width: "100%",
      height: "100%",
    },
    modalAvatarPlaceholder: {
      width: "100%",
      height: "100%",
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    loadingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.overlay,
      alignItems: "center",
      justifyContent: "center",
    },
    imageHintRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
      marginTop: spacing.xs,
      marginBottom: spacing.xs,
    },
    imageHint: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.primary,
    },
    editIconButton: {
      backgroundColor: colors.primary,
      width: 24,
      height: 24,
      borderRadius: 6,
      alignItems: "center",
      justifyContent: "center",
    },
    accountInfoSection: {
      marginBottom: spacing.lg,
      backgroundColor: colors.surface,
      borderRadius: 20,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    sectionLabel: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.textLight,
      marginBottom: spacing.xs,
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    accountInfoLabel: {
      fontSize: 12,
      color: colors.textLight,
      marginTop: spacing.sm,
      fontWeight: '600',
    },
    accountInfoValue: {
      fontSize: 15,
      color: colors.text,
      marginTop: 2,
      fontWeight: '500',
    },
    usernameSection: {
      marginBottom: spacing.md,
    },
    usernameHint: {
      fontSize: 12,
      color: colors.textLight,
      marginTop: 4,
    },
    cancelButtonText: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
    },
    section: {
      marginBottom: spacing.lg,
    },
    sectionNoMargin: {
      marginBottom: 0,
    },
    hint: {
      fontSize: 12,
      color: colors.textLight,
      marginTop: 4,
    },
    modalButtonsCompact: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: spacing.lg,
      marginBottom: spacing.xl,
      gap: spacing.md,
    },
  });
